import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';

import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { useLegacyTypes, useMemoFilterMulti } from '../hooks';

import { DataTableView } from '@components/shared/data-table';

import { getLoader } from '@selectors/data-table-selector';
import { getColumns, getOptimizedPortalRows } from '../selectors';

import { push } from 'connected-react-router';
import { fetchPortalData } from '../actions';

const PortalDataTable = ({ dataType, subType }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [legacyDataType, legacySubType] = useLegacyTypes(dataType, subType);
  const previousFetchTypes = useRef({});
  const pollInterval = useRef(null);
  const [pollCount, setPollCount] = useState(0);

  const stableProps = useMemo(() => ({ dataType, subType }), [dataType, subType]);
  const loader = useSelector(state => getLoader(state, stableProps));
  const dataTypesCount = useSelector(state => {
    if (legacySubType) {
      return state.dataTypes.stats?.stats?.count?.[legacyDataType]?.[legacySubType];
    }
    return state.dataTypes.stats?.stats?.count?.[legacyDataType];
  });
  const rows = useSelector(state => getOptimizedPortalRows(state, dataType, subType));
  const rowsEmpty = rows === null || rows.length === 0;
  const columns = useSelector(state => getColumns(state, dataType, subType));

  const [{ filterParams, orderParams, pageParams }, filterChanged] = useMemoFilterMulti(dataType, subType);

  const dispatchPush = useCallback(url => dispatch(push(url)), [dispatch]);

  const { current: prevFetch } = previousFetchTypes;
  const dataTypeChange = dataType !== prevFetch.dataType || subType !== prevFetch.subType;
  const doFetch = (
    filterChanged ||
    (dataTypeChange && (
      loader || // If loader is true, then there may be an incomplete fetch, so retry the fetch
      (dataTypesCount !== 0 && rowsEmpty)
    ))
  );

  const stopPolling = useCallback(() => {
    if (pollInterval.current) {
      clearInterval(pollInterval.current);
      pollInterval.current = null;
    }
  }, []);

  useEffect(() => {
    if (doFetch) {
      dispatch(fetchPortalData(dataType, subType, filterParams, orderParams, pageParams));
      previousFetchTypes.current = { dataType, subType };
    }
  }, [dispatch, doFetch, dataType, subType, filterParams, orderParams, pageParams]);

  useEffect(() => {
    if (location.state?.poll && dataType === 'batch') {
      pollInterval.current = setInterval(() => {
        setPollCount(prev => prev + 1);
        dispatch(fetchPortalData(dataType, subType, filterParams, orderParams, pageParams));
      }, 10000);
    }
    return stopPolling;
  }, [dispatch, dataType, subType, filterParams, location.state?.poll, orderParams, pageParams, setPollCount, stopPolling]);

  useEffect(() => {
    if (dataType === 'batch' && pollCount >= 6) {
      stopPolling();
    }
  }, [dataType, pollCount, stopPolling]);

  // Simplified getIsDataTypeEmpty for portal (uses numeric count)
  const getIsDataTypeEmpty = useCallback(() => dataTypesCount === 0, [dataTypesCount]);

  return (
    <DataTableView
      columns={columns}
      dataType={legacyDataType}
      subType={legacySubType}
      dataTypesCount={dataTypesCount}
      getIsDataTypeEmpty={getIsDataTypeEmpty}
      loader={loader || doFetch}
      push={dispatchPush}
      rows={rows}
    />
  );
};

PortalDataTable.propTypes = {
  dataType: PropTypes.string,
  subType: PropTypes.string
};

export default memo(PortalDataTable);
